import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useInlineTableEdit from 'src/components/shared/inline-table-edit/useInlineTableEdit'
import InlineEditableCell from 'src/components/shared/inline-table-edit/InlineEditableCell'
import { editVenueProduct } from 'src/features/VenueProductsSlice'
import {
  GROWTH_ENGINE_GUIDED_LANE_REASONS,
  emitGrowthEngineGuidedLaneProgress,
} from 'src/utils/growthEngineNudgeRefresh'
import { trackParLevelSaveIfChanged } from 'src/utils/parLevelAnalytics'
import { getParLevelForProduct } from 'src/utils/parLevelPaywall'
import {
  formatVenueProductInlineDisplayValue,
  formatVenueProductInlineEditValue,
  persistVenueProductInlineEdit,
} from './venueProductInlineEdit'

const INLINE_FIELD_CONFIG = {
  title: {
    inputMode: 'text',
    placeholder: 'Product title',
  },
  defaultQuantity: {
    inputMode: 'numeric',
    placeholder: '1',
  },
  parLevel: {
    inputMode: 'decimal',
    placeholder: '0',
    tooltipTitle:
      'Target stock on hand. Click to edit. Enter to save, Esc to cancel.',
  },
  nominalCode: {
    inputMode: 'text',
    placeholder: 'Nominal code',
  },
}

const useVenueProductInlineEdit = ({
  venueId,
  currentPlan = 'Free',
  showParLevelUpgradeModal,
}) => {
  const dispatch = useDispatch()
  const products = useSelector(state => state.venueProducts.products)

  const onParLevelSaved = useCallback(
    ({ venueProductId, previousParLevel, newParLevel }) => {
      trackParLevelSaveIfChanged({
        venueId,
        venueProductId,
        previousParLevel,
        newParLevel,
      })
      const previous = getParLevelForProduct({ parLevel: previousParLevel })
      const next = getParLevelForProduct({ parLevel: newParLevel })
      if (previous !== next && venueId) {
        emitGrowthEngineGuidedLaneProgress(
          venueId,
          GROWTH_ENGINE_GUIDED_LANE_REASONS.PAR_SAVED,
        )
      }
    },
    [venueId],
  )

  const onPersist = useCallback(
    async ({ record, field, rawValue }) => {
      const result = await persistVenueProductInlineEdit({
        dispatch,
        editVenueProduct,
        products,
        venueId,
        productId: record._id,
        field,
        rawValue,
        plan: currentPlan,
        onParLevelSaved,
      })

      if (result.paywallBlocked) {
        showParLevelUpgradeModal?.(result.paywallSource, record)
        return { ok: true, skipped: true }
      }

      return result
    },
    [
      currentPlan,
      dispatch,
      onParLevelSaved,
      products,
      showParLevelUpgradeModal,
      venueId,
    ],
  )

  const {
    inlineEditValue,
    inlineEditError,
    isActive,
    startInlineEdit,
    commitInlineEdit,
    onInlineInputKeyDown,
    setInlineEditValue,
  } = useInlineTableEdit({ onPersist })

  const renderInlineEditableCell = useCallback(
    (record, field) => {
      const config = INLINE_FIELD_CONFIG[field] ?? {}
      const active = isActive(record, field)

      return (
        <InlineEditableCell
          isActive={active}
          displayValue={formatVenueProductInlineDisplayValue(field, record)}
          value={inlineEditValue}
          error={inlineEditError}
          inputMode={config.inputMode}
          placeholder={config.placeholder}
          tooltipTitle={config.tooltipTitle}
          onStartEdit={() =>
            startInlineEdit(
              record,
              field,
              formatVenueProductInlineEditValue(field, record),
            )
          }
          onChange={setInlineEditValue}
          onCommit={commitInlineEdit}
          onKeyDown={onInlineInputKeyDown}
        />
      )
    },
    [
      commitInlineEdit,
      inlineEditError,
      inlineEditValue,
      isActive,
      onInlineInputKeyDown,
      setInlineEditValue,
      startInlineEdit,
    ],
  )

  return { renderInlineEditableCell, startInlineEdit }
}

export default useVenueProductInlineEdit
